/**
 * SupplierApplicationsTab
 * -----------------------
 * Master-admin view of "Become a Preferred Supplier" submissions from the
 * public product modal. Distinct from the agent/partner Applications tab.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Users, Trash2, Package } from "lucide-react";

const API = getApiUrl();
const STATUSES = ["pending", "reviewed", "contacted", "accepted", "rejected"];

function StatusBadge({ status }) {
  const cls =
    status === "accepted"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : status === "contacted"
      ? "bg-green-100 text-green-700 border-green-200"
      : status === "reviewed"
      ? "bg-blue-100 text-blue-700 border-blue-200"
      : status === "rejected"
      ? "bg-red-100 text-red-700 border-red-200"
      : "bg-amber-100 text-amber-700 border-amber-200";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border capitalize ${cls}`} data-testid={`supplier-app-status-${status}`}>
      {status}
    </span>
  );
}

export default function SupplierApplicationsTab() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/supplier-applications`, { withCredentials: true });
      setApplications(data.applications || []);
    } catch {
      toast.error("Failed to load supplier applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const changeStatus = async (app, status) => {
    setBusyId(app.application_id);
    try {
      await axios.put(
        `${API}/admin/supplier-applications/${app.application_id}/status`,
        { status },
        { withCredentials: true },
      );
      setApplications((prev) => prev.map((a) => a.application_id === app.application_id ? { ...a, status } : a));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (app) => {
    if (!confirm(`Delete application from "${app.name}"?`)) return;
    setBusyId(app.application_id);
    try {
      await axios.delete(`${API}/admin/supplier-applications/${app.application_id}`, { withCredentials: true });
      setApplications((prev) => prev.filter((a) => a.application_id !== app.application_id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="supplier-applications-tab">
      <div>
        <h2 className="font-serif text-2xl font-medium">Supplier Applications</h2>
        <p className="text-muted-foreground mt-1">
          &quot;Become a Preferred Supplier&quot; submissions from product pages on the site.
        </p>
      </div>

      {applications.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-border" data-testid="supplier-applications-empty">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-medium text-lg mb-2">No Applications Yet</h3>
          <p className="text-muted-foreground">
            When someone applies via &quot;Become a Supplier&quot; on a product, they&apos;ll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div
              key={app.application_id}
              className="bg-white border border-border rounded-lg p-5"
              data-testid={`supplier-application-${app.application_id}`}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="font-semibold text-lg" data-testid="supplier-app-name">{app.name}</h3>
                    {app.company_name && (
                      <span className="text-sm text-muted-foreground">({app.company_name})</span>
                    )}
                    <StatusBadge status={app.status} />
                    <span className="text-[10px] px-2 py-0.5 rounded border border-border text-muted-foreground capitalize">
                      {app.applicant_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-primary mb-3">
                    <Package className="h-3.5 w-3.5" />
                    <span data-testid="supplier-app-product">{app.product_name}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p className="font-medium">{app.email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{app.phone}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Area covered</p>
                      <p className="font-medium">{app.area_covered}</p>
                    </div>
                  </div>
                  {app.message && (
                    <div className="mt-4 p-3 bg-muted/30 rounded">
                      <p className="text-sm text-muted-foreground mb-1">Message</p>
                      <p className="text-sm">{app.message}</p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-4">
                    Applied: {new Date(app.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>

                <div className="flex flex-col gap-2 shrink-0 w-40">
                  <Select
                    value={app.status}
                    onValueChange={(v) => changeStatus(app, v)}
                    disabled={busyId === app.application_id}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid={`supplier-app-status-select-${app.application_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(app)}
                    disabled={busyId === app.application_id}
                    className="border-red-200 text-red-500 hover:bg-red-50 gap-1.5"
                    data-testid={`supplier-app-delete-${app.application_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
